import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Observable, Subscription, combineLatest } from 'rxjs';
import {
  AppCountry,
  CmsCountryObj,
  EstimateLabels,
  Labels,
  StatesObj,
} from '../app.models';
import {
  config as countryConfig,
  getCountryConfig,
} from '../config/country-config';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Store } from '@ngrx/store';
import {
  defaultCountry,
  isCashOutAllowed,
} from '../store/service-options/service-options.selectors';
import {
  FetchServiceOptions,
  ServiceOptionsRequest,
  ServiceOptionsState,
} from '../store/service-options';
import { isStatesAllowed } from '../config/app-config';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
} from 'rxjs/operators';
import { StringUtils } from '../shared/utils/string-utils';
import { CountryObj } from '../store/types';
import { FieldNames } from '../registration/registration.model';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { FormSearchUtils } from '../shared/utils/form-search-utils';

export interface EstimateValue {
  amount: number;
  country: string;
  state?: string;
  currency: string | undefined;
}

export interface StatesConfig {
  code: string;
  name: string;
}

@Component({
  selector: 'app-estimate',
  templateUrl: './estimate.component.html',
  styleUrls: ['./estimate.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EstimateComponent implements OnInit, OnDestroy {
  form: FormGroup;
  countryControl = new FormControl('', [Validators.required]);
  stateControl = new FormControl('', [Validators.required]);
  states: StatesConfig[];
  defaultStates: StatesConfig[];
  allowed: string = 'true';
  estimateLabels: EstimateLabels;
  csmCountries: any[];
  formSearchUtils = new FormSearchUtils();

  @Output() public valueChanges: EventEmitter<EstimateValue> =
    new EventEmitter<EstimateValue>();
  @Output() public submitted: EventEmitter<EstimateValue> =
    new EventEmitter<EstimateValue>();

  countries: AppCountry[] = countryConfig;
  countriesFromStore: any;
  defaultCountry: string;
  defaultCountryObj: CmsCountryObj;
  countryCodes: string[];

  private _watches: Subscription[] = [];
  filteredCountries: Observable<CountryObj[]>;
  filteredOptions: Observable<StatesObj[]>;
  countryArrowIcon: string = 'keyboard_arrow_down';
  stateArrowIcon: string = 'keyboard_arrow_down';
  arrowIcon = 'keyboard_arrow_down';
  disableCashInCountry: boolean = true;

  public _google: any;
  @Input() public set google(map: any) {
    this._google = map;
  }
  public get google() {
    return this._google;
  }

  @Input() locale!: string;
  @Input() indicativeRateCountry!: boolean;

  private _amount?: string;
  isCashoutAllowed$: Observable<boolean> | undefined;

  public _labels: any;
  @Input() public set labels(labels: Labels) {
    this._labels = labels;
    this.estimateLabels = labels?.stellar?.estimate;
    this.csmCountries = this.countries.map(
      (country) => labels?.stellar?.country[country?.code]
    );
  }
  public get labels() {
    return this._labels;
  }

  @Input() public set amount(amount: number | string) {
    if (typeof amount === 'number') {
      this._amount = amount.toFixed(2);
    } else {
      this._amount =
        (amount &&
          !Number.isNaN(parseFloat(amount)) &&
          parseFloat(amount).toFixed(2)) ||
        undefined;
    }

    if (this._amount !== this.form?.get('amount')?.value) {
      if (this._disableAmount) {
        this.form?.get('amount')?.enable();
      }

      this.form?.get('amount')?.setValue(this._amount);

      if (this._disableAmount) {
        this.form?.get('amount')?.disable();
      }
    }
  }
  public get amount() {
    return this._amount!;
  }

  private _disableAmount?: boolean;
  @Input() public set disableAmount(disable: boolean | string) {
    this._disableAmount = coerceBooleanProperty(disable);
  }
  public get disableAmount() {
    return this._disableAmount!;
  }

  @Input() flow!: string;
  @Input() token!: string;

  @Input() public set options(options: ServiceOptionsState) {
    this._serviceOptions = options;
    this.disableCashInCountry =
      options?.uiConfigs[0]?.disableCashInCountry !== undefined
        ? options?.uiConfigs[0]?.disableCashInCountry
        : true;
    this._receiveCurrencies =
      options?.serviceOptions[0]?.sendCurrencyCodes ||
      options?.serviceOptions[0]?.payoutCurrencyCodes;
    this.countryCodes =
      options?.cashOutCountries.length > 1
        ? options?.cashOutCountries
        : options?.cashInCountries;
    const localCurrency = options?.serviceOptions[0]?.localCurrencyCode;
    // if receives currencies contains local currency then consider local currency otherwise take first element of receive cureencies
    this._receiveCurrency = !!this._receiveCurrencies
      ? this._receiveCurrencies.some((currency) => currency === localCurrency)
        ? localCurrency
        : this._receiveCurrencies[0]
      : 'USD';
    this.countriesFromStore = options?.countriesDetails;
  }

  private _country?: string;
  @Input() public set countryDetails(countryDetails: CmsCountryObj) {
    this.defaultCountryObj = {
      code: countryDetails?.code,
      name: countryDetails?.name,
      iso2code: countryDetails?.iso2code,
      states: countryDetails.states,
    };
    this._country = countryDetails?.code;
    this._countryName = countryDetails?.name;
    const states =
      this._country && isStatesAllowed(this._country)
        ? countryDetails && countryDetails.states
        : [];
    this._updateStateControl(states);
    this.defaultStates =
      this._country && this._country === 'USA'
        ? this.filterStates(states)
        : states;
    this.states = this.defaultStates;

    if (this._country !== this.form?.get('country')?.value) {
      if (this.disableCashInCountry) {
        this.form?.get('country')?.enable();
      }

      this.form?.get('country')?.setValue(this._country);

      if (this.disableCashInCountry && this.flow !== 'withdraw') {
        this.form?.get('country')?.disable();
      }
    }
  }

  _serviceOptions?: ServiceOptionsState;
  _receiveCurrencies: string[] = [];
  public get receiveCurrencies() {
    return this._receiveCurrencies;
  }

  _receiveCurrency?: string;
  public get receiveCurrency() {
    return this._receiveCurrency;
  }

  _countryName?: string;
  public get countryName() {
    return this._countryName;
  }

  public get iso2Code() {
    return this._country && getCountryConfig(this._country)?.iso2Code;
  }

  public get value() {
    const currentVal = this.form?.getRawValue();
    return (
      currentVal && {
        ...currentVal,
        amount: currentVal.amount ? parseFloat(currentVal.amount) : undefined,
        currency: this.receiveCurrency,
      }
    );
  }

  private _endpoint: string;
  @Input() public set endpoint(endpointValue: string) {
    this._endpoint = endpointValue;
  }
  public get endpoint() {
    return this._endpoint;
  }

  @ViewChildren(MatAutocompleteTrigger)
  triggers!: QueryList<MatAutocompleteTrigger>;

  constructor(
    private _fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private store: Store<any>
  ) {
    this.form = this._fb.group({
      state: this.stateControl,
      country: this.countryControl,
      amount: new FormControl('', [Validators.required, Validators.min(1)]),
    });
  }

  public ngOnInit(): void {
    this.store.select(isCashOutAllowed).subscribe((allowed) => {
      this.allowed = allowed;
    });

    this.store.select(defaultCountry).subscribe((defaultCountry) => {
      this.defaultCountry = defaultCountry;
    });

    if (this.flow === 'deposit' || this.states.length === 0) {
      this.form.removeControl('state');
    }

    this.filteredCountries = this.countryControl.valueChanges.pipe(
      startWith(''),
      map((value) => this.countriesFilter(value || ''))
    );

    combineLatest([this.filteredCountries, this.countryControl.valueChanges])
      .pipe(debounceTime(0), distinctUntilChanged())
      .subscribe(([country, value]: [CountryObj[], string | null]) => {
        const filterValue = StringUtils.removeDiacriticsAndConvertToLowercase(
          value!
        );
        if (filterValue && !country.length) {
          this.form.get(FieldNames.country)?.setErrors({ invalidOption: true });
        } else if (!filterValue && country.length) {
          this.form.get(FieldNames.country)?.setErrors({ required: true });
        } else if (
          filterValue ===
          StringUtils.removeDiacriticsAndConvertToLowercase(country[0]?.name)
        ) {
          const matchedOption = country.filter(
            (country: CountryObj) =>
              StringUtils.removeDiacriticsAndConvertToLowercase(
                country?.name
              ) === filterValue
          );
          if (matchedOption.length > 1) {
            const selectedOption =
              matchedOption.find(
                (country: CountryObj) =>
                  StringUtils.removeDiacriticsAndConvertToLowercase(
                    country?.name
                  ) === 'united states of america'
              ) || matchedOption[0];
            this.form
              .get(FieldNames.country)
              ?.setValue(selectedOption?.code, { emitEvent: false });
            this.setDefaultCountryStatePair(selectedOption?.name);
            this.form.get(FieldNames.country)?.setErrors(null);
            this.closePanel();
          } else if (matchedOption.length === 1) {
            this.form
              .get(FieldNames.country)
              ?.setValue(matchedOption[0]?.code, { emitEvent: false });
            this.setDefaultCountryStatePair(matchedOption[0]?.name);
            this.form.get(FieldNames.country)?.setErrors(null);
            this.closePanel();
          }
          this.form.get(FieldNames.country)?.setErrors(null);
        } else {
          this.form.get(FieldNames.country)?.setErrors({ invalidOption: true });
        }
      });

    this.filteredOptions = this.stateControl.valueChanges.pipe(
      startWith(''),
      map((value) => this.statesFilter(value || ''))
    );

    combineLatest([this.filteredOptions, this.stateControl.valueChanges])
      .pipe(debounceTime(0), distinctUntilChanged())
      .subscribe(([state, value]: [StatesObj[], string | null]) => {
        const errorKeys: string[] = ['required', 'invalidOption'];
        const filterValue = value!;
        this.formSearchUtils.handleFilteredStateOption(
          filterValue,
          FieldNames.state,
          state,
          this.form,
          this.triggers,
          errorKeys
        );
      });

    if (this._disableAmount) {
      this.form.get('amount')?.disable();
    }

    if (this.disableCashInCountry && this.flow !== 'withdraw') {
      this.form.get('country')?.disable();
    }

    this._watches.push(
      this.form.valueChanges
        .pipe(
          distinctUntilChanged(
            (a, b) => JSON.stringify(a) === JSON.stringify(b)
          )
        ) // To avoid infinite loop
        .subscribe((change) => this._handleFormChange(change))
    );
  }

  setDefaultCountryStatePair(countryName: string | null) {
    if (countryName) {
      const countryCode = this.getCountryCode(countryName);
      const isSupportedCountry: any = this.countryCodes.find(
        (code) => code === countryCode
      );
      if (
        countryCode &&
        this.defaultCountry !== countryCode &&
        isSupportedCountry
      ) {
        this.onCountryChange(countryCode);
      }
      if (
        countryCode &&
        this.defaultCountry === countryCode &&
        isSupportedCountry
      ) {
        if (!!this.defaultStates) {
          this.states = this.defaultStates;
        }
      }
      if (!isSupportedCountry) {
        this.states = [];
        this.cdr.detectChanges();
      }
    }
  }

  private statesFilter(value: string): StatesConfig[] {
    const filterValue =
      StringUtils.removeDiacriticsAndConvertToLowercase(value);
    return this.states.filter((option: StatesObj) => {
      return (
        (option?.name &&
          StringUtils.removeDiacriticsAndConvertToLowercase(
            option?.name
          ).startsWith(filterValue)) ||
        (option?.code &&
          StringUtils.removeDiacriticsAndConvertToLowercase(
            option?.code
          ).startsWith(filterValue))
      );
    });
  }

  private countriesFilter(value: any): AppCountry[] {
    const filterValue =
      StringUtils.removeDiacriticsAndConvertToLowercase(value);
    return this.countriesFromStore.filter(
      (option: { name: string; code: string }) => {
        return (
          (option?.name &&
            StringUtils.removeDiacriticsAndConvertToLowercase(
              option?.name
            ).startsWith(filterValue)) ||
          (option?.code &&
            StringUtils.removeDiacriticsAndConvertToLowercase(
              option?.code
            ).startsWith(filterValue))
        );
      }
    );
  }

  private countryPanelToggle(isOpen: boolean) {
    if (isOpen) {
      this.countryArrowIcon = 'keyboard_arrow_up';
    } else {
      this.countryArrowIcon = 'keyboard_arrow_down';
    }
  }
  public statePanelToggle(isOpen: boolean): void {
    if (isOpen) {
      this.stateArrowIcon = 'keyboard_arrow_up';
    } else {
      this.stateArrowIcon = 'keyboard_arrow_down';
    }
  }

  public getSelectedOptionLabel(options: any, key: string, fieldValue: any) {
    if (!fieldValue) {
      return '';
    }
    const index = options?.findIndex(
      (option: any) => option[key] === fieldValue
    );
    return options[index]?.name;
  }

  public onCountryChange(selectedCountry: any) {
    if (
      this.defaultCountry !== selectedCountry &&
      (this.flow === 'withdraw' || !this.disableCashInCountry)
    ) {
      const payload: ServiceOptionsRequest = {
        token: this.token,
        language: this.locale?.substring(0, 2),
        operation: this.flow,
        selectedWithdrawCountry: selectedCountry,
      };
      this.store.dispatch(
        FetchServiceOptions({
          getOptionsRequest: payload,
          endpoint: this.endpoint,
        })
      );
    }
  }

  public formatAmount(): void {
    const control = this.form?.get('amount')!;
    this.amount = control.value;

    if (this._amount !== control.value) {
      control.patchValue(this._amount);
    }
  }

  public changeCurrency(rc: any): void {
    this._receiveCurrency = rc;
  }

  backToWalletDone() {
    setTimeout(() => {
      window.close();
    }, 400);
  }

  private _handleFormChange(estValue: EstimateValue): void {
    if (estValue.state) {
      const stateCode = this.getStateCode(estValue.state);
      if (stateCode) {
        estValue.state = stateCode;
      } else {
        this.form.get('state')?.setErrors({ invalidOption: true });
      }
    }
    if (estValue.country) {
      const countryCode = this.getCountryCode(estValue.country);
      // 1. If CC not exist,then its not a valid country option
      // 2. If CC exisits anf not supported, then it's not a supported country Ex: Nepal
      if (countryCode) {
        estValue.country = countryCode;
        const isSupportedCountry = this.countryCodes.find(
          (code) => code === countryCode
        );
        if (!isSupportedCountry) {
          this.form.get('country')?.setErrors({ notSupported: true });
        } else {
          this.form.get('country')?.setErrors(null);
        }
      } else {
        this.form.get('country')?.setErrors({ invalidOption: true });
        this.countryPanelToggle(false);
      }
    }
    this.valueChanges.emit(estValue);
  }

  getStateCode(stateName: string): string | undefined {
    return !!this.states && stateName
      ? this.states.find(
          (state) => state.name === stateName || state.code === stateName
        )?.code
      : '';
  }

  getCountryCode(countryValue: string): any {
    const countryCode =
      this.csmCountries && countryValue
        ? this.csmCountries.find(
            (country: any) =>
              StringUtils.removeDiacriticsAndConvertToLowercase(
                country?.name
              ) ==
              StringUtils.removeDiacriticsAndConvertToLowercase(countryValue)
          )?.code
        : '';
    if (countryCode) {
      return countryCode;
    } else {
      return this.csmCountries && countryValue
        ? this.csmCountries.find(
            (country: any) =>
              StringUtils.removeDiacriticsAndConvertToLowercase(
                country.code
              ) ===
              StringUtils.removeDiacriticsAndConvertToLowercase(countryValue)
          )?.code
        : '';
    }
  }

  filterStates(states: StatesConfig[]) {
    const filteredStates =
      !!states &&
      this._serviceOptions?.uiConfigs[0]?.notSupportedUSStates &&
      states.filter(
        (state) =>
          !this._serviceOptions?.uiConfigs[0]?.notSupportedUSStates.includes(
            state.code
          )
      );
    return filteredStates ? filteredStates : states;
  }

  formatSubmitValue(value: EstimateValue) {
    if (value.state) {
      value.state = this.getStateCode(value.state);
    }
    if (value.country) {
      value.country = this.getCountryCode(value.country);
    }
    return value;
  }

  private _updateStateControl(states: StatesConfig[]): void {
    if (
      this.flow === 'withdraw' &&
      states.length > 0 &&
      !this.form.get('state')
    ) {
      // States exist for country
      this.form.addControl('state', this.stateControl);
    } else if (this.flow === 'deposit' || states.length === 0) {
      // States DON'T exist for country
      this.form.removeControl('state');
    }
  }

  public ngOnDestroy(): void {
    this._watches.forEach((subscription) => subscription.unsubscribe());
  }
  private closePanel() {
    this.triggers.forEach((trigger: MatAutocompleteTrigger) => {
      if (trigger.panelOpen) {
        setTimeout(() => trigger.closePanel());
      }
    });
  }
}
